import { useEffect, useState } from 'react';
import {
  computeTourScheduleState,
  dayLabelInZone,
  formatTimeOfDayInZone,
  TourScheduleBlock,
} from '../tours/tourSchedule';
import { zoneShortName } from '../advisory/advisorySchedule';

// Editor time pickers hold "HH:mm" display strings; the preview parses them
// directly to wall-clock milliseconds (no date involved, so no DST skew).
function parseTimeDisplay(display: string | null | undefined): number | null {
  if (!display) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(display.trim());
  if (!m) return null;
  const hour = parseInt(m[1], 10);
  const minute = parseInt(m[2], 10);
  if (hour > 23 || minute > 59) return null;
  return hour * 3600000 + minute * 60000;
}

interface TourNextRunPreviewProps {
  /** The editor's currentTour: timeZoneId, isEnabled, tourAvailabilityList. */
  tour: any;
  /**
   * Number of stops attached to the saved tour, or null when unknown (new
   * unsaved tour, or the stop list is loading/repurposed). At 0 the preview
   * states the tour will not run — the server never creates a guard tour for
   * a stop-less tour, regardless of its availability window.
   */
  stopCount?: number | null;
}

/**
 * Live "Next run" readback under the Available Hours editor, computed from the
 * time blocks as they are edited (before saving) with the same schedule rules
 * the server uses to create tours. Times are shown in the TOUR's time zone —
 * the zone the admin is configuring against. This is a neutral readback, not
 * validation: a wrong AM/PM surfaces because the admin reads an unexpected
 * next run here instead of discovering a missed patrol later.
 */
export function TourNextRunPreview({ tour, stopCount = null }: TourNextRunPreviewProps) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const zoneId: string | null = tour.timeZoneId || null;
  const blocks: TourScheduleBlock[] = (tour.tourAvailabilityList || []).map((group: any) => ({
    startDayOfWeek: group.startDayOfWeek ?? null,
    startTime: parseTimeDisplay(group.startTimeDisplay),
    endDayOfWeek: group.endDayOfWeek ?? null,
    endTime: parseTimeDisplay(group.endTimeDisplay),
  }));
  const hasAnyInput = blocks.length > 0;
  if (!hasAnyInput) return null;

  if (!zoneId) {
    return (
      <div className="kst-nextrun-preview kst-nextrun-preview--neutral">
        <div className="kst-nextrun-preview-main">Select a time zone to preview the next run.</div>
      </div>
    );
  }

  const state = computeTourScheduleState(blocks, zoneId, now);
  if (state.status === 'none') {
    // Blocks exist but none is complete yet (missing day or time).
    return (
      <div className="kst-nextrun-preview kst-nextrun-preview--neutral">
        <div className="kst-nextrun-preview-main">Complete a time block to preview the next run.</div>
      </div>
    );
  }

  const zoneLabel = zoneShortName(zoneId, now);
  const zoneSuffix = zoneLabel ? ` (${zoneLabel})` : '';
  const startDay = dayLabelInZone(state.start!, zoneId, now);
  const endDay = dayLabelInZone(state.end!, zoneId, now);
  const startTime = formatTimeOfDayInZone(state.start!, zoneId);
  const endTime = formatTimeOfDayInZone(state.end!, zoneId);

  const startLabel = startDay.relative
    ? `${startDay.relative} (${startDay.weekdayShort})`
    : startDay.weekdayLong;
  let windowText = `${startLabel} ${startTime} → ${endDay.weekdayShort} ${endTime}${zoneSuffix}`;
  if (state.status !== 'in-progress' && startDay.daysAway >= 2) {
    windowText += ` · in ${startDay.daysAway} days`;
  }

  if (!tour.isEnabled) {
    return (
      <div className="kst-nextrun-preview kst-nextrun-preview--neutral">
        <div className="kst-nextrun-preview-main">Tour is disabled — it will not be scheduled.</div>
        <div className="kst-nextrun-preview-detail">Next run if enabled: {windowText}</div>
      </div>
    );
  }

  if (stopCount === 0) {
    return (
      <div className="kst-nextrun-preview kst-nextrun-preview--neutral">
        <div className="kst-nextrun-preview-main">No tour stops are attached — this tour will not run.</div>
        <div className="kst-nextrun-preview-detail">Next run once a stop is added: {windowText}</div>
      </div>
    );
  }

  const runText =
    state.status === 'in-progress'
      ? `In progress now · until ${endDay.relative ?? endDay.weekdayShort} ${endTime}${zoneSuffix}`
      : `Next run: ${windowText}`;

  const highlight = state.status === 'in-progress' || startDay.relative === 'today';
  return (
    <div className={`kst-nextrun-preview kst-nextrun-preview--${highlight ? 'ok' : 'neutral'}`}>
      <div className="kst-nextrun-preview-main">{runText}</div>
      <div className="kst-nextrun-preview-detail">
        Preview updates as you edit · shows the soonest upcoming run across all time blocks ·
        matches exactly how tours will be scheduled.
      </div>
    </div>
  );
}
